import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { BookOpen, Brain, Star, TrendingUp, ChevronLeft } from "lucide-react";
import { toast } from "react-toastify";
import { getVocabStats } from "../../services/VocabService";
import VocabLayout from "./VocabLayout";
import SkeletonGrid from "./SkeletonGrid";

const StatCard = ({ icon: Icon, iconColor, bgColor, title, value, subtitle }) => (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex items-start gap-4">
    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 ${bgColor}`}>
      <Icon size={22} className={iconColor} />
    </div>
    <div>
      <p className="text-sm text-gray-500">{title}</p>
      <p className="text-3xl font-bold text-gray-900 mt-0.5">{value}</p>
      <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>
    </div>
  </div>
);

export default function VocabDashboard() {
  const [stats, setStats] = useState({ total: 0, toReview: 0, mastered: 0 });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const res = await getVocabStats();
        if (res?.data) setStats(res.data);
      } catch (err) {
        toast.error(err?.response?.data?.message || "Không thể tải thống kê");
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  const masteredPct = useMemo(
    () => (stats.total > 0 ? Math.round((stats.mastered / stats.total) * 100) : 0),
    [stats.mastered, stats.total]
  );

  // Memoized — rebuilt only when stats or masteredPct changes
  const CARDS = useMemo(() => [
    {
      icon: BookOpen,
      iconColor: "text-blue-600",
      bgColor: "bg-blue-50",
      title: "Tổng từ vựng",
      value: stats.total,
      subtitle: "Từ đã thêm",
    },
    {
      icon: Brain,
      iconColor: "text-orange-600",
      bgColor: "bg-orange-50",
      title: "Cần ôn tập",
      value: stats.toReview,
      subtitle: "Đến hạn hôm nay",
    },
    {
      icon: Star,
      iconColor: "text-green-600",
      bgColor: "bg-green-50",
      title: "Đã thành thạo",
      value: stats.mastered,
      subtitle: "SRS cấp độ 5",
    },
    {
      icon: TrendingUp,
      iconColor: "text-purple-600",
      bgColor: "bg-purple-50",
      title: "Tỉ lệ thành thạo",
      value: `${masteredPct}%`,
      subtitle: "Trên tổng số từ",
    },
  ], [stats, masteredPct]);

  const pageHeader = (
    <>
      <Link
        to="/vocabulary"
        className="flex items-center gap-1 text-gray-500 hover:text-gray-800 transition-colors text-sm"
      >
        <ChevronLeft size={18} />
        Danh sách từ
      </Link>
      <div className="flex items-center gap-2 ml-auto">
        <BookOpen size={18} className="text-red-600" />
        <span className="font-semibold text-gray-900 text-sm">Dashboard Từ vựng</span>
      </div>
    </>
  );

  return (
    <VocabLayout header={pageHeader}>
      <h2 className="text-xl font-bold text-gray-900 mb-6">Tổng quan học từ vựng</h2>

      {/* Stats grid */}
      {loading ? (
        <SkeletonGrid
          count={4}
          itemClassName="h-28"
          gridClassName="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {CARDS.map((card) => (
            <StatCard key={card.title} {...card} />
          ))}
        </div>
      )}

      {/* Progress section */}
      {!loading && (
        <div className="mt-8 bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Tiến độ thành thạo</h3>
          <div className="flex items-center gap-4">
            <div className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-red-400 to-red-600 rounded-full transition-all duration-700"
                style={{ width: `${masteredPct}%` }}
              />
            </div>
            <span className="text-sm font-semibold text-gray-700 w-12 text-right">
              {masteredPct}%
            </span>
          </div>
          <p className="text-xs text-gray-400 mt-2">
            {stats.mastered} / {stats.total} từ đã thành thạo
          </p>
        </div>
      )}

      {/* CTA buttons */}
      <div className="mt-8 flex flex-wrap gap-4">
        <Link
          to="/vocabulary/learn"
          className="flex items-center gap-2 px-5 py-3 rounded-2xl bg-red-600 text-white font-medium hover:bg-red-700 transition-colors"
        >
          <Brain size={18} />
          Bắt đầu ôn tập ({stats.toReview} thẻ)
        </Link>
        <Link
          to="/vocabulary"
          className="flex items-center gap-2 px-5 py-3 rounded-2xl border border-gray-200 text-gray-700 font-medium hover:bg-gray-50 transition-colors"
        >
          <BookOpen size={18} />
          Quản lý từ vựng
        </Link>
      </div>
    </VocabLayout>
  );
}
